import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { PayWithPointsModal, Spinner } from 'components';
import { COUPON_TYPES } from 'coupon-common';
import { actions as customerProfileActions } from 'coupon-common/src/modules/customerProfile';

import { RouteNames } from '../../../constants';
import { getRemainingTime } from '../../../utils/getRemainingTime';
import messages from './messages';
import styles from './ReserveCoupon.module.scss';

const ReservedCoupon = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { reservedData } = location.state || {};

  const details = reservedData?.coupon_details || {};
  const isPaymentByPoints = details.payment_by_points;
  const isShowPriceOnCoupon = details.show_price_on_coupon;

  const [isPayBPVisible, setIsPayBPVisible] = useState(false);
  const [isPointsModalVisible, setIsPointsModalVisible] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  // Ticks every second so the "Expires in..." countdown (now including
  // seconds) actually counts down live instead of showing a static value
  // frozen at first render.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!reservedData?.expiry_time) return undefined;
    const timerId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timerId);
  }, [reservedData?.expiry_time]);

  let dealAmount;
  let newTotal;
  if (details.type === COUPON_TYPES.AMOUNT_OFF) {
    dealAmount = `$${details.amount_off} OFF`;
    newTotal = (details.price - details.amount_off).toFixed(2);
  } else if (details.type === COUPON_TYPES.PERCENTAGE_OFF) {
    dealAmount = `${Math.round(details.percentage_off)}% OFF`;
    newTotal = (details.price - (details.price * details.percentage_off) / 100).toFixed(2);
  } else if (details.type === COUPON_TYPES.POINTS_BACK) {
    dealAmount = `${details.points_back} Points Back`;
    newTotal = details.price;
  }

  const pointsRequired = Math.ceil((newTotal || 0) * 100);

  const customerProfile = useSelector(state => state.customerProfile);
  const dispatch = useDispatch();

  const getCustomerProfile = useCallback(() => {
    dispatch(customerProfileActions.getCustomerProfile());
  }, [dispatch]);

  useEffect(() => {
    getCustomerProfile();
  }, [getCustomerProfile]);

  // getRemainingTime(expiry_time) is assumed to only return
  // hours/minutes — seconds are derived here directly from the raw
  // millisecond diff instead, so this doesn't depend on that util also
  // exposing a seconds field.
  const remainingTime = useMemo(() => {
    if (!reservedData?.expiry_time) {
      return { hours: 0, minutes: 0, seconds: 0, expired: true };
    }
    const base = getRemainingTime(reservedData.expiry_time);
    if (base.expired) {
      return { ...base, seconds: 0 };
    }
    const diffMs = new Date(reservedData.expiry_time).getTime() - now;
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
    const seconds = totalSeconds % 60;
    return { ...base, seconds, expired: totalSeconds <= 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservedData?.expiry_time, now]);

  const images = details.image_url || [];
  const categoryName = details.category?.name || (typeof details.category === 'string' ? details.category : null);

  // Falls back gracefully — profile field name isn't guaranteed across
  // environments, so try the common variants before giving up.
  const customerName =
    customerProfile?.data?.full_name || customerProfile?.data?.name || customerProfile?.data?.first_name || 'Guest';

  const handleButtonPress = () => setModalVisible(true);

  const applyCoupon = (payByPoints = false) => {
    setIsPayBPVisible(false);
    const id = details.id;
    navigate(`${RouteNames.COUPONS_APPLY}/${id}?payByPoints=${payByPoints}`);
  };

  const onUseCoupons = () => applyCoupon();

  const handleConfirm = async () => {
    setModalVisible(false);
    setIsRedeeming(true);
    try {
      // onUseCoupons navigates away on success (applyCoupon -> navigate),
      // which unmounts this component immediately. Only reset isRedeeming
      // in the catch branch — resetting it unconditionally in a `finally`
      // ran setState after the component was already gone.
      await onUseCoupons();
    } catch (err) {
      setIsRedeeming(false);
    }
  };

  const onPayByPointsPress = () => {
    const points = customerProfile?.data?.total_points ?? 0;
    const price = details.price ?? null;

    if (!reservedData || !details) {
      // eslint-disable-next-line no-alert
      window.alert('Coupon details are unavailable. Please try again later.');
      return;
    }

    if (points < price * 100) {
      // eslint-disable-next-line no-alert
      window.alert("Sorry, you don't have enough points to pay for this coupon.");
    } else {
      applyCoupon(true);
    }
  };

  const gotoPayWithPoints = () => {
    if (!customerProfile?.data?.has_active_subscription) {
      // eslint-disable-next-line no-alert
      const goToAccount = window.confirm('Please upgrade your membership to pay with points.\n\nGo to Account Settings?');
      if (goToAccount) navigate(RouteNames.ACCOUNT);
    } else {
      setIsPayBPVisible(true);
    }
  };

  const openPointsModal = () => setIsPointsModalVisible(true);
  const closePointsModal = () => setIsPointsModalVisible(false);

  const hasEnoughPoints = (customerProfile?.data?.total_points ?? 0) >= pointsRequired;

  if (!reservedData) {
    return (
      <div className={styles.page}>
        <div className={styles.wrap} style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
          <Spinner show size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.wrap}>
        {/* ─── Hero card: image (left) + ticket-style details (right) ─── */}
        <div className={styles.heroCard}>
          <div className={styles.heroImagePane}>
            {images.length > 0 && (
              <img src={images[imageIndex]?.image_url} alt={details.title || ''} className={styles.heroImage} />
            )}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.swiperNav} ${styles.prev}`}
                  onClick={() => setImageIndex(i => (i === 0 ? images.length - 1 : i - 1))}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`${styles.swiperNav} ${styles.next}`}
                  onClick={() => setImageIndex(i => (i === images.length - 1 ? 0 : i + 1))}
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}
            {categoryName && (
              <div className={styles.heroBadgeRow}>
                <span className={styles.badgePillWhite}>{categoryName}</span>
              </div>
            )}
          </div>

          <div className={styles.heroContentPane}>
            {/* Expires-in text centered, Active badge pinned to the right —
                same active-badge treatment as CouponsDetails, always shown
                here (unless the reservation has actually expired) since
                this page's whole purpose is a live reservation. */}
            <div className={styles.topRow}>
              <span className={styles.topRowSpacer} aria-hidden="true" />
              <p className={styles.expiryDate}>
                {remainingTime.expired
                  ? 'Expired'
                  : remainingTime.hours === 0
                    ? `Expires in ${remainingTime.minutes}:${String(remainingTime.seconds).padStart(2, '0')} min`
                    : `Expires in ${remainingTime.hours}:${String(remainingTime.minutes).padStart(2, '0')}:${String(remainingTime.seconds).padStart(2, '0')} hours`}
              </p>

              {!remainingTime.expired && (
                <span className={styles.activeBadge}>
                  <span className={styles.activeDot} />
                  Active
                </span>
              )}
            </div>

            {/* Boarding-pass style ticket rows: label pinned left, value pinned right */}
            <div className={styles.ticketRows}>
              <div className={styles.ticketRowItem}>
                <span className={styles.ticketLabel}>Name</span>
                <span className={styles.ticketValue}>{customerName}</span>
              </div>

              <div className={styles.ticketRowItem}>
                <span className={styles.ticketLabel}>Deal</span>
                <span className={styles.ticketValue}>{details.title}</span>
              </div>

              <div className={styles.ticketRowItem}>
                <span className={styles.ticketLabel}>Deal</span>
                <span className={styles.ticketValue}>{dealAmount}</span>
              </div>

              <div className={styles.ticketRowItem}>
                <span className={styles.ticketLabel}>Price</span>
                <span className={styles.ticketValue}>${details.price}</span>
              </div>

              {details.type !== COUPON_TYPES.POINTS_BACK && (
                <div className={styles.ticketRowItem}>
                  <span className={styles.ticketLabel}>Now</span>
                  <span className={styles.ticketValueNow}>${newTotal}</span>
                </div>
              )}
            </div>

            <p className={styles.instructions}>
              <FormattedMessage {...messages.instructions} />
            </p>

            <div className={styles.actionRow}>
              <button type="button" className={styles.closeBtn} onClick={() => navigate(-1)}>
                <FormattedMessage {...messages.close} />
              </button>
              <button type="button" className={styles.redeemBtn} onClick={handleButtonPress} disabled={isRedeeming}>
                {isRedeeming ? <Spinner show size="sm" /> : <FormattedMessage {...messages.redeemNow} />}
              </button>
            </div>

            {isPaymentByPoints && isShowPriceOnCoupon && (
              <button type="button" className={styles.payPointsLink} onClick={openPointsModal}>
                <FormattedMessage {...messages.payWithPoints} />
              </button>
            )}
          </div>
        </div>

        <PayWithPointsModal
          isVisible={isPayBPVisible}
          setIsVisible={setIsPayBPVisible}
          data={details}
          onPayByPointsPress={onPayByPointsPress}
        />

        {isPointsModalVisible && (
          <div className={styles.modalOverlay} onClick={closePointsModal}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
              <p className={styles.pointsText}>
                {hasEnoughPoints ? 'Purchase this coupon with your points' : 'You do not have enough points for this coupon'}
              </p>
              {hasEnoughPoints && <p className={styles.pointsRequired}>{pointsRequired} Points</p>}
              <div className={styles.modalButtons}>
                <button type="button" className={styles.modalBtnNo} onClick={closePointsModal}>
                  <FormattedMessage {...messages.close} />
                </button>
                <button
                  type="button"
                  className={styles.modalBtnYes}
                  onClick={() => {
                    closePointsModal();
                    gotoPayWithPoints();
                  }}
                  disabled={!hasEnoughPoints}
                >
                  <FormattedMessage {...messages.payWithPoints} />
                </button>
              </div>
            </div>
          </div>
        )}

        {isModalVisible && (
          <div className={styles.modalOverlay} onClick={() => setModalVisible(false)}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 className={styles.modalTitle}>
                <FormattedMessage {...messages.redeemNowModalTitle} />
              </h2>
              <p className={styles.modalMessage}>
                <FormattedMessage {...messages.redeemNowModalMessage} />
              </p>
              <div className={styles.modalButtons}>
                <button
                  type="button"
                  className={styles.modalBtnNo}
                  onClick={() => setModalVisible(false)}
                  disabled={isRedeeming}
                >
                  <FormattedMessage {...messages.no} />
                </button>
                <button type="button" className={styles.modalBtnYes} onClick={handleConfirm} disabled={isRedeeming}>
                  {isRedeeming ? <Spinner show size="sm" /> : <FormattedMessage {...messages.yes} />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservedCoupon;